/**
 * PORT_CONFIG — defines flow connection ports (handles) per element type.
 *   type: 'source' = flow output  |  'target' = flow input
 *   pos:  'top' | 'bottom' | 'left' | 'right'
 *   offset: 0-100 (% along the side, for multiple ports on the same side)
 *
 * Elements not listed here get NO flow handles (sensors, displays, shapes, motor).
 */
export const PORT_CONFIG = {
    tank:              [{ id: 'in',      pos: 'top',    type: 'target' },
                        { id: 'out',     pos: 'bottom', type: 'source' }],

    silo:              [{ id: 'in',      pos: 'top',    type: 'target' },
                        { id: 'out',     pos: 'bottom', type: 'source' }],

    hopper_screw:      [{ id: 'in',      pos: 'top',    type: 'target' },
                        { id: 'out',     pos: 'bottom', type: 'source' }],

    // pump: inlets on left, outlets on right — counts driven by config
    // Always uses numbered IDs (in1, in2... out1, out2...) so handles
    // don't get re-registered when count changes.
    pump: (data) => {
        const inlets  = Math.max(1, parseInt(data?.config?.inlet_count  ?? 1));
        const outlets = Math.max(1, parseInt(data?.config?.outlet_count ?? 1));
        const ports = [];

        for (let i = 0; i < inlets; i++) {
            const pct = inlets === 1 ? 50 : 20 + (i / (inlets - 1)) * 60;
            ports.push({ id: `in${i + 1}`, pos: 'left', type: 'target', offset: pct });
        }
        for (let i = 0; i < outlets; i++) {
            const pct = outlets === 1 ? 50 : 20 + (i / (outlets - 1)) * 60;
            ports.push({ id: `out${i + 1}`, pos: 'right', type: 'source', offset: pct });
        }

        return ports;
    },

    motor:             [],

    compressor:        [{ id: 'out',     pos: 'right',  type: 'source' }],

    chiller:           [{ id: 'chw_in',  pos: 'left',   type: 'target', offset: 70 },
                        { id: 'chw_out', pos: 'left',   type: 'source', offset: 30 },
                        { id: 'cw_in',   pos: 'right',  type: 'target', offset: 30 },
                        { id: 'cw_out',  pos: 'right',  type: 'source', offset: 70 }],

    heat_exchanger:    [{ id: 'in1',     pos: 'left',   type: 'target', offset: 30 },
                        { id: 'in2',     pos: 'left',   type: 'target', offset: 70 },
                        { id: 'out1',    pos: 'right',  type: 'source', offset: 30 },
                        { id: 'out2',    pos: 'right',  type: 'source', offset: 70 }],

    reactor:           [{ id: 'in',      pos: 'top',    type: 'target' },
                        { id: 'out',     pos: 'bottom', type: 'source' }],

    // Bidirectional valves: one source handle per side.
    // connectionMode="loose" in the editor lets these connect in any direction.
    valve_gate:        [{ id: 'left',  pos: 'left',  type: 'source' },
                        { id: 'right', pos: 'right', type: 'source' }],

    valve_ball:        [{ id: 'left',  pos: 'left',  type: 'source' },
                        { id: 'right', pos: 'right', type: 'source' }],

    valve_butterfly:   [{ id: 'left',  pos: 'left',  type: 'source' },
                        { id: 'right', pos: 'right', type: 'source' }],

    valve_check:       [{ id: 'in',      pos: 'left',   type: 'target' },
                        { id: 'out',     pos: 'right',  type: 'source' }],

    valve_3way:        [{ id: 'in',      pos: 'left',   type: 'target' },
                        { id: 'outa',    pos: 'top',    type: 'source' },
                        { id: 'outb',    pos: 'bottom', type: 'source' }],

    regulator:         [{ id: 'in',      pos: 'left',   type: 'target' },
                        { id: 'out',     pos: 'right',  type: 'source' }],

    sensor_flow:       [{ id: 'in',      pos: 'left',   type: 'target' },
                        { id: 'out',     pos: 'right',  type: 'source' }],

    // sensor_temp, sensor_pressure, sensor_level, sensor_humidity → no flow ports

    // cyclone: air+material enters right (target) at stub height; material exits bottom (source)
    cyclone: (data) => {
        const h = data?.height || 80;
        const stubOffsetPct = (4 + 10) / h * 100; // bodyY=4, stub center at +10
        return [
            { id: 'in',  pos: 'right',  type: 'target', offset: stubOffsetPct },
            { id: 'out', pos: 'bottom', type: 'source' },
        ];
    },

    // ribbon_blender: dynamic — 1 or 2 bottom outlets depending on gate_count
    ribbon_blender: (data) => {
        const gates = parseInt(data?.config?.gate_count ?? 0);
        const ports = [{ id: 'in', pos: 'top', type: 'target' }];
        if (gates >= 2) {
            // Match SVG gate positions: cx ± bodyW * 0.22, where bodyW = width - 16
            const w = data?.width || 100;
            const cx = w / 2;
            const bodyW = w - 16;
            const leftPct  = (cx - bodyW * 0.22) / w * 100;
            const rightPct = (cx + bodyW * 0.22) / w * 100;
            ports.push({ id: 'out1', pos: 'bottom', type: 'source', offset: leftPct });
            ports.push({ id: 'out2', pos: 'bottom', type: 'source', offset: rightPct });
        } else {
            ports.push({ id: 'out', pos: 'bottom', type: 'source' });
        }
        return ports;
    },

    // cooler_blender: product enters top (target), hot air+dust exits left (source→cyclone), cooled product exits bottom (source)
    cooler_blender:    [{ id: 'product_in', pos: 'top',    type: 'target' },
                        { id: 'air_out',    pos: 'left',   type: 'source' },
                        { id: 'out',        pos: 'bottom', type: 'source' },
                        { id: 'chw_in',     pos: 'right',  type: 'target', offset: 30 },
                        { id: 'chw_out',    pos: 'right',  type: 'source', offset: 70 }],

    granulator:        [{ id: 'in',      pos: 'top',    type: 'target' },
                        { id: 'out',     pos: 'bottom', type: 'source' }],

    screw_conveyor:    [{ id: 'in',      pos: 'left',   type: 'target' },
                        { id: 'out',     pos: 'right',  type: 'source' }],

    // bucket_elevator: horizontal layout — 1-2 inlets (top) + 1-N outlets (bottom)
    bucket_elevator: (data) => {
        const inlets  = parseInt(data?.config?.inlet_count  ?? 1);
        const outlets = parseInt(data?.config?.outlet_count ?? 1);
        const w = data?.width || 120;
        const pulleyR = 10;
        const leftCX  = pulleyR + 4;
        const rightCX = w - pulleyR - 4;
        const ports = [];

        // Inlet(s) at top — spread between pulleys, match SVG inletXs
        if (inlets === 1) {
            ports.push({ id: 'in', pos: 'top', type: 'target' });
        } else {
            for (let i = 0; i < inlets; i++) {
                const pct = i / (inlets - 1);
                const px = leftCX + pct * (rightCX - leftCX);
                ports.push({ id: `in${i + 1}`, pos: 'top', type: 'target', offset: px / w * 100 });
            }
        }

        // Outlet(s) at bottom — spread across full width, match SVG outletXs
        if (outlets === 1) {
            ports.push({ id: 'out', pos: 'bottom', type: 'source' });
        } else {
            for (let i = 0; i < outlets; i++) {
                const pct = i / (outlets - 1);
                const px = 8 + pct * (w - 16);
                ports.push({ id: `out${i + 1}`, pos: 'bottom', type: 'source', offset: px / w * 100 });
            }
        }

        // Return port on right: product that completes a full revolution without being
        // discharged returns here (typically back to the granulator / feed hopper).
        ports.push({ id: 'return', pos: 'right', type: 'source' });

        return ports;
    },

    packager_single:   [{ id: 'in',      pos: 'top',    type: 'target' }],
    packager_multi:    [{ id: 'in',      pos: 'top',    type: 'target' }],
    packager_doypack:  [{ id: 'in',      pos: 'top',    type: 'target' }],

    // End-of-line packaging
    box_former:        [{ id: 'out',        pos: 'right',  type: 'source' }],

    strip_folder:      [{ id: 'ristras_in', pos: 'left',   type: 'target', offset: 30 },
                        { id: 'carton_in',  pos: 'left',   type: 'target', offset: 70 },
                        { id: 'out',        pos: 'right',  type: 'source' }],

    box_sealer:        [{ id: 'in',         pos: 'left',   type: 'target' },
                        { id: 'out',        pos: 'right',  type: 'source' }],

    boiler:            [{ id: 'water_in',  pos: 'bottom', type: 'target' },
                        { id: 'steam_out', pos: 'top',    type: 'source' },
                        { id: 'fuel_in',   pos: 'left',   type: 'target', offset: 70 }],

    water_heater:      [{ id: 'cold_in',   pos: 'bottom', type: 'target' },
                        { id: 'hot_out',   pos: 'top',    type: 'source' }],

    v_blender:         [{ id: 'in1',       pos: 'top',    type: 'target', offset: 25 },
                        { id: 'in2',       pos: 'top',    type: 'target', offset: 75 },
                        { id: 'out',       pos: 'bottom', type: 'source' }],

    sieve:             [{ id: 'in',        pos: 'top',    type: 'target' },
                        { id: 'out',       pos: 'bottom', type: 'source' },
                        { id: 'reject',    pos: 'right',  type: 'source', offset: 50 }],

    belt_conveyor:     [{ id: 'in',        pos: 'left',   type: 'target' },
                        { id: 'out',       pos: 'right',  type: 'source' }],

    cip_skid:          [{ id: 'supply_out', pos: 'right',  type: 'source' },
                        { id: 'return_in', pos: 'left',   type: 'target' }],

    custom_machine:    [{ id: 'in',      pos: 'left',   type: 'target' },
                        { id: 'out',     pos: 'right',  type: 'source' }],

};

/**
 * SIGNAL_CONFIG — defines what variables can be bound per element type.
 *   key:      internal key stored in config.variables[key]
 *   label:    shown in inspector
 *   dataType: 'number' | 'boolean' | 'string' — filters available variables
 *
 * alarm_banner is handled separately (multi-variable picker).
 * Elements not listed get no variable binding section.
 */
export const SIGNAL_CONFIG = {
    tank:            [{ key: 'level',    label: 'scada.sig.level',          dataType: 'number' }],

    silo:            [{ key: 'level',    label: 'scada.sig.level',          dataType: 'number' },
                      { key: 'weight',   label: 'scada.sig.weight',         dataType: 'number' }],

    hopper_screw:    [{ key: 'state',              label: 'scada.sig.state',            dataType: 'boolean' },
                      { key: 'level',              label: 'scada.sig.level',            dataType: 'number' },
                      { key: 'speed',              label: 'scada.sig.speedScrew',       dataType: 'number' },
                      { key: 'requesting_product', label: 'scada.sig.requestingProduct',dataType: 'boolean' },
                      { key: 'error',              label: 'scada.sig.error',            dataType: 'boolean' }],

    pump:            [{ key: 'state',      label: 'scada.sig.state',          dataType: 'boolean' },
                      { key: 'speed',      label: 'scada.sig.speed',          dataType: 'number' },
                      { key: 'pressure',   label: 'scada.sig.pressure',       dataType: 'number' },
                      { key: 'cip_active', label: 'scada.sig.cipActive',      dataType: 'boolean' },
                      { key: 'error',      label: 'scada.sig.error',          dataType: 'boolean' }],

    motor:           [{ key: 'state',    label: 'scada.sig.state',          dataType: 'boolean' },
                      { key: 'speed',    label: 'scada.sig.speed',          dataType: 'number' },
                      { key: 'current',  label: 'scada.sig.current',        dataType: 'number' },
                      { key: 'error',    label: 'scada.sig.error',          dataType: 'boolean' }],

    compressor:      [{ key: 'state',    label: 'scada.sig.state',          dataType: 'boolean' },
                      { key: 'pressure', label: 'scada.sig.pressure',       dataType: 'number' },
                      { key: 'error',    label: 'scada.sig.error',          dataType: 'boolean' }],

    chiller:         [{ key: 'state',         label: 'scada.sig.state',         dataType: 'boolean' },
                      { key: 'temp_supply',   label: 'scada.sig.tempSupply',    dataType: 'number'  },
                      { key: 'temp_return',   label: 'scada.sig.tempReturn',    dataType: 'number'  },
                      { key: 'pressure_high', label: 'scada.sig.pressureHigh',  dataType: 'number'  },
                      { key: 'pressure_low',  label: 'scada.sig.pressureLow',   dataType: 'number'  },
                      { key: 'error',         label: 'scada.sig.fault',         dataType: 'boolean' }],

    heat_exchanger:  [{ key: 'temp_in',  label: 'scada.sig.tempIn',         dataType: 'number' },
                      { key: 'temp_out', label: 'scada.sig.tempOut',        dataType: 'number' }],

    reactor:         [{ key: 'temp',     label: 'scada.sig.temp',           dataType: 'number' },
                      { key: 'level',    label: 'scada.sig.level',          dataType: 'number' },
                      { key: 'agitator', label: 'scada.sig.agitator',       dataType: 'boolean' },
                      { key: 'error',    label: 'scada.sig.error',          dataType: 'boolean' }],

    valve_gate:      [{ key: 'state',    label: 'scada.sig.stateSimple',    dataType: 'boolean' }],
    valve_ball:      [{ key: 'state',    label: 'scada.sig.stateSimple',    dataType: 'boolean' }],
    valve_butterfly: [{ key: 'state',    label: 'scada.sig.stateSimple',    dataType: 'boolean' }],
    valve_check:     [],
    valve_3way:      [{ key: 'state',    label: 'scada.sig.position3way',   dataType: 'boolean' }],

    regulator:       [{ key: 'position', label: 'scada.sig.position',       dataType: 'number' }],

    sensor_temp:     [{ key: 'value',    label: 'scada.sig.temp',           dataType: 'number' }],
    sensor_pressure: [{ key: 'value',    label: 'scada.sig.pressure',       dataType: 'number' }],
    sensor_flow:     [{ key: 'value',    label: 'scada.sig.flow',           dataType: 'number' }],
    sensor_level:    [{ key: 'value',    label: 'scada.sig.level',          dataType: 'number' }],
    sensor_humidity: [{ key: 'value',    label: 'scada.sig.humidity',       dataType: 'number' }],

    cyclone:         [{ key: 'state',    label: 'scada.sig.state',          dataType: 'boolean' },
                      { key: 'speed',    label: 'scada.sig.speed',          dataType: 'number' },
                      { key: 'current',  label: 'scada.sig.current',        dataType: 'number' },
                      { key: 'error',    label: 'scada.sig.error',          dataType: 'boolean' }],

    ribbon_blender:  [{ key: 'state',      label: 'scada.sig.state',          dataType: 'boolean' },
                      { key: 'speed',      label: 'scada.sig.speed',          dataType: 'number' },
                      { key: 'current',    label: 'scada.sig.current',        dataType: 'number' },
                      { key: 'gate_1',     label: 'scada.sig.gate1',          dataType: 'boolean' },
                      { key: 'gate_2',     label: 'scada.sig.gate2',          dataType: 'boolean' },
                      { key: 'cip_active', label: 'scada.sig.cipActive',      dataType: 'boolean' },
                      { key: 'error',      label: 'scada.sig.error',          dataType: 'boolean' }],

    cooler_blender:  [{ key: 'state',        label: 'scada.sig.state',       dataType: 'boolean' },
                      { key: 'speed',        label: 'scada.sig.speed',       dataType: 'number' },
                      { key: 'temp',         label: 'scada.sig.temp',        dataType: 'number' },
                      { key: 'temp_ready',   label: 'scada.sig.tempReady',   dataType: 'boolean' },
                      { key: 'current',      label: 'scada.sig.current',     dataType: 'number' },
                      { key: 'level',        label: 'scada.sig.level',       dataType: 'number' },
                      { key: 'outlet_state', label: 'scada.sig.outletState', dataType: 'boolean' },
                      { key: 'cip_active',   label: 'scada.sig.cipActive',   dataType: 'boolean' },
                      { key: 'error',        label: 'scada.sig.error',       dataType: 'boolean' }],

    granulator:      [{ key: 'state',    label: 'scada.sig.state',          dataType: 'boolean' },
                      { key: 'speed',    label: 'scada.sig.speed',          dataType: 'number' },
                      { key: 'current',  label: 'scada.sig.current',        dataType: 'number' },
                      { key: 'error',    label: 'scada.sig.error',          dataType: 'boolean' }],

    screw_conveyor:  [{ key: 'state',    label: 'scada.sig.state',          dataType: 'boolean' },
                      { key: 'speed',    label: 'scada.sig.speed',          dataType: 'number' },
                      { key: 'current',  label: 'scada.sig.current',        dataType: 'number' },
                      { key: 'error',    label: 'scada.sig.error',          dataType: 'boolean' }],

    bucket_elevator: [{ key: 'state',    label: 'scada.sig.state',          dataType: 'boolean' },
                      { key: 'speed',    label: 'scada.sig.speed',          dataType: 'number' },
                      { key: 'current',  label: 'scada.sig.current',        dataType: 'number' },
                      { key: 'load_kg',  label: 'scada.sig.loadKg',         dataType: 'number' },
                      { key: 'error',    label: 'scada.sig.error',          dataType: 'boolean' }],

    packager_single: [{ key: 'state',              label: 'scada.sig.state',            dataType: 'boolean' },
                      { key: 'speed',              label: 'scada.sig.speed',            dataType: 'number' },
                      { key: 'count',              label: 'scada.sig.count',            dataType: 'number' },
                      { key: 'requesting_product', label: 'scada.sig.requestingProduct',dataType: 'boolean' },
                      { key: 'error',              label: 'scada.sig.error',            dataType: 'boolean' }],

    packager_multi:  [{ key: 'state',              label: 'scada.sig.state',            dataType: 'boolean' },
                      { key: 'speed',              label: 'scada.sig.speed',            dataType: 'number' },
                      { key: 'count',              label: 'scada.sig.count',            dataType: 'number' },
                      { key: 'requesting_product', label: 'scada.sig.requestingProduct',dataType: 'boolean' },
                      { key: 'error',              label: 'scada.sig.error',            dataType: 'boolean' }],

    packager_doypack:[{ key: 'state',              label: 'scada.sig.state',            dataType: 'boolean' },
                      { key: 'speed',              label: 'scada.sig.speed',            dataType: 'number' },
                      { key: 'count',              label: 'scada.sig.count',            dataType: 'number' },
                      { key: 'requesting_product', label: 'scada.sig.requestingProduct',dataType: 'boolean' },
                      { key: 'error',              label: 'scada.sig.error',            dataType: 'boolean' }],

    // End-of-line packaging
    box_former:      [{ key: 'state',  label: 'scada.sig.state',  dataType: 'boolean' },
                      { key: 'speed',  label: 'scada.sig.speed',  dataType: 'number'  },
                      { key: 'count',  label: 'scada.sig.count',  dataType: 'number'  },
                      { key: 'error',  label: 'scada.sig.error',  dataType: 'boolean' }],

    strip_folder:    [{ key: 'state',              label: 'scada.sig.state',              dataType: 'boolean' },
                      { key: 'speed',              label: 'scada.sig.speed',              dataType: 'number'  },
                      { key: 'count',              label: 'scada.sig.count',              dataType: 'number'  },
                      { key: 'ristras_count',      label: 'scada.sig.ristrasCount',       dataType: 'number'  },
                      { key: 'carton_weight_kg',   label: 'scada.sig.cartonWeightKg',     dataType: 'number'  },
                      { key: 'requesting_ristras', label: 'scada.sig.requestingRistras',  dataType: 'boolean' },
                      { key: 'requesting_carton',  label: 'scada.sig.requestingCarton',   dataType: 'boolean' },
                      { key: 'error',              label: 'scada.sig.error',              dataType: 'boolean' }],

    box_sealer:      [{ key: 'state',  label: 'scada.sig.state',  dataType: 'boolean' },
                      { key: 'speed',  label: 'scada.sig.speed',  dataType: 'number'  },
                      { key: 'count',  label: 'scada.sig.count',  dataType: 'number'  },
                      { key: 'error',  label: 'scada.sig.error',  dataType: 'boolean' }],

    display_digital: [{ key: 'value',    label: 'scada.sig.value',          dataType: 'number' }],
    display_trend:   [{ key: 'value',    label: 'scada.sig.value',          dataType: 'number' }],
    led_indicator:   [{ key: 'state',    label: 'scada.sig.stateSimple',    dataType: 'boolean' }],

    boiler:          [{ key: 'state',    label: 'scada.sig.state',          dataType: 'boolean' },
                      { key: 'pressure', label: 'scada.sig.pressure',       dataType: 'number'  },
                      { key: 'temp',     label: 'scada.sig.temp',           dataType: 'number'  },
                      { key: 'error',    label: 'scada.sig.fault',          dataType: 'boolean' }],

    water_heater:    [{ key: 'state',    label: 'scada.sig.state',          dataType: 'boolean' },
                      { key: 'temp',     label: 'scada.sig.temp',           dataType: 'number'  },
                      { key: 'error',    label: 'scada.sig.fault',          dataType: 'boolean' }],

    v_blender:       [{ key: 'state',    label: 'scada.sig.state',          dataType: 'boolean' },
                      { key: 'speed',    label: 'scada.sig.speed',          dataType: 'number'  },
                      { key: 'current',  label: 'scada.sig.current',        dataType: 'number'  },
                      { key: 'error',    label: 'scada.sig.error',          dataType: 'boolean' }],

    sieve:           [{ key: 'state',    label: 'scada.sig.state',          dataType: 'boolean' },
                      { key: 'speed',    label: 'scada.sig.speedVibration', dataType: 'number' },
                      { key: 'current',  label: 'scada.sig.current',        dataType: 'number'  },
                      { key: 'error',    label: 'scada.sig.error',          dataType: 'boolean' }],

    belt_conveyor:   [{ key: 'state',       label: 'scada.sig.state',          dataType: 'boolean' },
                      { key: 'speed',       label: 'scada.sig.speed',          dataType: 'number'  },
                      { key: 'current',     label: 'scada.sig.current',        dataType: 'number'  },
                      { key: 'queue_level', label: 'scada.sig.queueLevel',     dataType: 'number'  },
                      { key: 'error',       label: 'scada.sig.error',          dataType: 'boolean' }],

    cip_skid:        [{ key: 'state',          label: 'scada.sig.state',          dataType: 'boolean' },
                      { key: 'cip_phase',      label: 'scada.sig.cipPhase',       dataType: 'number'  },
                      { key: 'pump_state',     label: 'scada.sig.pumpState',      dataType: 'boolean' },
                      { key: 'level_water',    label: 'scada.sig.levelWater',     dataType: 'number'  },
                      { key: 'level_caustic',  label: 'scada.sig.levelCaustic',   dataType: 'number'  },
                      { key: 'level_acid',     label: 'scada.sig.levelAcid',      dataType: 'number'  },
                      { key: 'temp',           label: 'scada.sig.temp',           dataType: 'number'  },
                      { key: 'error',          label: 'scada.sig.fault',          dataType: 'boolean' }],

    // alarm_banner: handled separately in inspector (multi-variable)
    // custom_machine: handled separately (states_config)
    // shapes, text_label, image_embed, pipe_h, pipe_v: no variables
};
